import React, { useCallback, useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';

function authHeaders() {
  const t = localStorage.getItem('token');
  return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' };
}

/**
 * Кнопка «Режим Бога»: ввод супер-пароля → временные права администратора.
 */
export default function GodModeControl({ compact = false, onChanged } = {}) {
  const { state, dispatch } = useApp();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [allowed, setAllowed] = useState(false);
  const active = !!(state.user?.godMode);

  const applyToken = useCallback((token, godMode, role) => {
    if (token) localStorage.setItem('token', token);
    const prev = state.user || {};
    const next = godMode
      ? {
          ...prev,
          godMode: true,
          roleBeforeGod: prev.roleBeforeGod || prev.role,
          role: 'admin',
        }
      : {
          ...prev,
          godMode: false,
          role: role || prev.roleBeforeGod || prev.role,
          roleBeforeGod: undefined,
        };
    if (!godMode) delete next.roleBeforeGod;
    try {
      localStorage.setItem('user', JSON.stringify(next));
    } catch (_) {}
    dispatch({ type: 'SET_USER', payload: next });
    if (typeof onChanged === 'function') onChanged(!!godMode);
  }, [dispatch, onChanged, state.user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !state.user) {
      setAllowed(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/god-mode/status', { headers: authHeaders() });
        const data = await r.json().catch(() => ({}));
        if (cancelled || !r.ok) {
          if (!cancelled) setAllowed(false);
          return;
        }
        setAllowed(!!data.allowed);
        if (!data.allowed && state.user?.godMode) {
          dispatch({
            type: 'SET_USER',
            payload: {
              ...state.user,
              godMode: false,
              role: data.role || state.user.roleBeforeGod || state.user.role,
            },
          });
          return;
        }
        if (!!data.active !== !!state.user?.godMode) {
          dispatch({
            type: 'SET_USER',
            payload: data.active
              ? { ...state.user, godMode: true, roleBeforeGod: state.user.roleBeforeGod || state.user.role, role: 'admin' }
              : { ...state.user, godMode: false, role: data.role || state.user.roleBeforeGod || state.user.role },
          });
        }
      } catch (_) {
        if (!cancelled) setAllowed(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user?.id, state.user?.username]);

  const unlock = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await fetch('/api/god-mode/unlock', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || 'Не удалось включить режим Бога');
      applyToken(data.token, true, data.role);
      setPassword('');
      setOpen(false);
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const lock = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await fetch('/api/god-mode/lock', {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.error || 'Не удалось выключить');
      applyToken(data.token, false, data.role);
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  if (!state.user || !allowed) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => (active ? lock() : setOpen(true))}
        disabled={busy}
        title={active ? 'Выключить режим Бога' : 'Включить режим Бога (супер-пароль)'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: compact ? '6px 10px' : '8px 12px',
          borderRadius: 10,
          border: active ? '1px solid rgba(239,68,68,.55)' : '1px solid rgba(148,163,184,.35)',
          background: active ? 'rgba(239,68,68,.18)' : 'rgba(15,23,42,.45)',
          color: active ? '#fecaca' : '#e2e8f0',
          fontWeight: 800,
          fontSize: compact ? '0.78rem' : '0.85rem',
          cursor: busy ? 'wait' : 'pointer',
        }}
      >
        {active ? '⚡ Режим Бога вкл.' : '⚡ Режим Бога'}
      </button>

      {open && (
        <div
          onClick={() => !busy && setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 300000,
            background: 'rgba(0,0,0,.55)',
            display: 'grid',
            placeItems: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(400px, 100%)',
              borderRadius: 16,
              background: '#0f172a',
              border: '1px solid rgba(255,255,255,.12)',
              padding: 18,
              color: '#e2e8f0',
              boxShadow: '0 24px 60px rgba(0,0,0,.45)',
            }}
          >
            <div style={{ fontWeight: 900, fontSize: '1.05rem', marginBottom: 8 }}>Режим Бога</div>
            <p style={{ margin: '0 0 12px', fontSize: '.85rem', opacity: 0.75, lineHeight: 1.45 }}>
              Введите супер-пароль, чтобы получить полные права: видеть все доски, переименовывать и удалять чужие канбаны и другое.
            </p>
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') unlock(); }}
              placeholder="Супер-пароль"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,.16)',
                background: 'rgba(0,0,0,.35)',
                color: '#fff',
                fontSize: '1rem',
                marginBottom: 10,
              }}
            />
            {error && (
              <div style={{ color: '#fca5a5', fontSize: '.85rem', fontWeight: 700, marginBottom: 10 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={busy}
                onClick={() => setOpen(false)}
                style={{
                  padding: '8px 12px', borderRadius: 10, border: 'pointer',
                  border: '1px solid rgba(255,255,255,.15)', background: 'transparent', color: '#cbd5e1', fontWeight: 700,
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                disabled={busy || !password}
                onClick={unlock}
                style={{
                  padding: '8px 14px', borderRadius: 10, cursor: busy || !password ? 'not-allowed' : 'pointer',
                  border: 'none', background: 'linear-gradient(135deg,#ef4444,#b91c1c)', color: '#fff', fontWeight: 800,
                }}
              >
                {busy ? 'Проверка…' : 'Войти'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
